import SweetAlert from 'sweetalert2';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggingService } from './logging.service';
import { PointeeSnackbarComponent } from '../components/pointee-snackbar/pointee-snackbar.component';
import { html, TrustedHtml } from '../helpers/html.helper';
import { TextHelper } from '../helpers/text.helper';

/**
 * Title or message of a popup. SweetAlert2 renders both as raw DOM, so a plain string - which is
 * usually built from entity names the customer controls - is escaped, and markup has to be opted
 * into with the `html` tagged template, which escapes everything interpolated into it.
 */
export type PopupText = string | TrustedHtml;

@Injectable({ providedIn: 'root' })
export class PopupService {
  constructor(private matSnackBar: MatSnackBar, private loggingService: LoggingService) {}

  async showInfo(message: PopupText, title?: PopupText, okButtonText?: string, standardClose = true, customClass?: string): Promise<void> {
    await SweetAlert.fire({
      title: this.toHtml(title),
      html: this.toHtml(message),
      showConfirmButton: true,
      confirmButtonText: this.toHtml(okButtonText || 'OK'),
      showCloseButton: false,
      closeButtonHtml: this.getCloseButtonHtml(),
      allowOutsideClick: standardClose,
      allowEscapeKey: standardClose,
      customClass: {
        popup: customClass,
        confirmButton: 'swal2-confirm link',
      },
    });
  }

  async showCannotDelete(deleteMessage: PopupText, title?: PopupText, confirmBtnText?: string): Promise<void> {
    await SweetAlert.fire({
      title: this.toHtml(title),
      html: this.toHtml(deleteMessage),
      showConfirmButton: false,
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      allowOutsideClick: true,
      allowEscapeKey: true,
      footer: html`<button type="button" class="btn btn-link" id="deleteBtnConfirm">${confirmBtnText}</button>`.value,
      customClass: {
        popup: 'wide',
        footer: 'swal2-footer',
        title: 'swal2-title text-left',
        htmlContainer: 'swal2-html-container text-left',
      },
      willOpen: () => {
        document.getElementById('deleteBtnConfirm').addEventListener('click', () => this.close());
      },
      willClose: () => {
        document.getElementById('deleteBtnConfirm').removeEventListener('click', () => this.close());
      },
    });
  }

  async showOkCancelQuestion(title: PopupText, message: PopupText, okButtonText?: string, cancelButtonText?: string, isPositive = false): Promise<boolean> {
    const sweetAlert = SweetAlert.mixin({
      title: this.toHtml(title),
      html: this.toHtml(message),
      showConfirmButton: true,
      confirmButtonText: this.toHtml(okButtonText ?? 'OK'),
      showCancelButton: true,
      cancelButtonText: this.toHtml(cancelButtonText ?? 'Cancel'),
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      reverseButtons: true,
      customClass: {
        confirmButton: `swal2-confirm ${isPositive ? 'primary' : 'danger'}`,
      },
    });
    return (await sweetAlert.fire()).isConfirmed;
  }

  async showDeleteQuestion(title: PopupText, message: PopupText, deleteButtonText?: string, cancelButtonText?: string): Promise<boolean> {
    const sweetAlert = SweetAlert.mixin({
      title: this.toHtml(title),
      html: this.toHtml(message),
      showConfirmButton: true,
      confirmButtonText: this.toHtml(deleteButtonText ?? 'Delete'),
      showCancelButton: true,
      cancelButtonText: this.toHtml(cancelButtonText ?? 'Cancel'),
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      reverseButtons: true,
      customClass: {
        confirmButton: 'swal2-confirm danger',
      },
    });
    return (await sweetAlert.fire()).isConfirmed;
  }

  async showDeleteQuestionWithConfirm(title: PopupText, message: PopupText, inputLabel: string, confirmValue: string): Promise<boolean> {
    const sweetAlert = SweetAlert.mixin({
      title: this.toHtml(title),
      input: 'text',
      inputLabel,
      html: this.toHtml(message),
      showConfirmButton: true,
      confirmButtonText: 'Delete',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      reverseButtons: true,
      width: '500px',
      didOpen: () => {
        const confirmBtn = sweetAlert.getConfirmButton();
        confirmBtn.disabled = true;
        const input = sweetAlert.getInput();
        input.addEventListener('input', () => {
          confirmBtn.disabled = input.value.trim().toLowerCase() !== confirmValue.toLowerCase();
        });
      },
      customClass: {
        confirmButton: 'swal2-confirm danger',
      },
    });
    return (await sweetAlert.fire()).isConfirmed;
  }

  async showCustomAlertWithRadioButtons(title: PopupText, options: Record<string, string>, selected: string, okButtonText?: string, cancelButtonText?: string, isPositive = false, message?: PopupText): Promise<string> {
    const result = await SweetAlert.fire<string>({
      title: this.toHtml(title),
      html: this.toHtml(message),
      input: 'radio',
      inputOptions: this.toHtmlOptions(options),
      inputValue: selected,
      showConfirmButton: true,
      confirmButtonText: this.toHtml(okButtonText ?? 'OK'),
      showCancelButton: true,
      cancelButtonText: this.toHtml(cancelButtonText ?? 'Cancel'),
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      reverseButtons: true,
      customClass: {
        confirmButton: `swal2-confirm ${isPositive ? 'primary' : 'danger'}`,
      },
      didOpen: popup => {
        // With no pre-selected option, force an explicit choice: keep the confirm button disabled until a radio is picked.
        if (selected != null) {
          return;
        }
        const confirmButton = SweetAlert.getConfirmButton();
        confirmButton.disabled = true;
        popup.querySelectorAll<HTMLInputElement>('input[type="radio"]')
          .forEach(radio => radio.addEventListener('change', () => (confirmButton.disabled = false)));
      },
    });

    return result.isConfirmed ? result.value : null;
  }

  async showQuestion(title: string, inputValue: string, confirmLabel: string): Promise<string> {
    const sweetAlert = SweetAlert.mixin({
      customClass: {
        confirmButton: 'swal2-confirm primary',
      },
      didOpen: () => {
        sweetAlert.getInput()?.addEventListener('keyup', e => {
          const { value } = e.target as HTMLInputElement;
          if ((value || '').trim().length === 0) {
            sweetAlert.disableButtons();
          } else {
            sweetAlert.enableButtons();
          }
        });
      },
    });

    const result = await sweetAlert.fire<string>({
      html: html`<label for="swal-input">${title}:</label>`.value,
      input: 'text',
      inputValue,
      inputAttributes: {
        id: 'swal-input',
        autocapitalize: 'off',
        class: 'pointee-input',
      },
      inputValidator: r => (!r || r.trim().length < 1) && 'enter valid input',
      confirmButtonText: this.toHtml(confirmLabel),
      showCloseButton: true,
      closeButtonHtml: this.getCloseButtonHtml(),
      allowOutsideClick: true,
      backdrop: true,
      showClass: {
        popup: 'animate__animated animate__fadeIn',
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOut',
      },
    });

    return result.value;
  }

  showSnackBar(message: string, duration?: number): void {
    this.matSnackBar.openFromComponent(PointeeSnackbarComponent, {
      duration: duration || 10000,
      horizontalPosition: 'start',
      verticalPosition: 'bottom',
      panelClass: 'snackbar',
      data: message,
    });
  }

  showError(message: string, error?: any): void {
    this.showSnackBar(message);
    this.loggingService.logError(message, error);
  }

  private close(): void {
    SweetAlert.close();
  }

  /**
   * SweetAlert2 parses what it is given into DOM nodes, so anything that is not explicitly trusted
   * markup is escaped first - an entity name such as `<img src=x onerror=...>` has to show as text.
   */
  private toHtml(text: PopupText): string {
    return text instanceof TrustedHtml ? text.value : TextHelper.encodeHtml(text);
  }

  /** The labels of radio options end up in the DOM the same way the message does. */
  private toHtmlOptions(options: Record<string, string>): Record<string, string> {
    return Object.fromEntries(Object.entries(options).map(([value, label]) => [value, TextHelper.encodeHtml(label)]));
  }

  private getCloseButtonHtml(): string {
    return '<div style="width:16px;height:16px;mask:url(assets/svg/shared/close.svg) no-repeat center/16px;background-color:#6b7280;"></div>';
  }
}
